import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import DependingObjects from '../../components/psc/lib/DependingObjects'
import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import TextArea from '../../components/psc/lib/TextArea'

import useCadmObject from '../../hooks/useCadmObject'
import useBrowserTabTitle from '../../hooks/useBrowserTabTitle'

const ProjectConfigurationScheme = () => {
    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/project_configuration_schemes' + (isNew ? '' : '/' + objID)

    const {
        cadmObjects,
    } = useCadmObject()

    // Fields
    const [name, setName] = useState('')

    useBrowserTabTitle(isNew ? 'New Configuration Scheme' : (name || 'Configuration Scheme'))
    const [description, setDescription] = useState('')
    const [projectConfigurationSchemeReferenceStructures, setProjectConfigurationSchemeReferenceStructures] = useState([])
    const [projectConfigurationSchemeTypecodeGroupMappings, setProjectConfigurationSchemeTypecodeGroupMappings] = useState([])
    const [webTypecodeMapList, setWebTypecodeMapList] = useState([])
    const [webRefStructList, setWebRefStructList] = useState([])

    // Read-only
    const [isUsed, setIsUsed] = useState(false)
    const [dependingObjects, setDependingObjects] = useState([])
    const [usedInProjects, setUsedInProjects] = useState([])

    // Defaults
    const [documentStartNumberingFrom, setDocumentStartNumberingFrom] = useState(1)
    const [recordStartNumberingFrom, setRecordStartNumberingFrom] = useState(1)
    const [allTypecodeGroupMappings, setAllTypecodeGroupMappings] = useState([])
    const [allReferenceStructures, setAllReferenceStructures] = useState([])

    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [cloneActive, setCloneActive] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        fetch('/v0/typecode_group_mappings', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setAllTypecodeGroupMappings(json.results)
            })
        fetch('/v0/reference_structures', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setAllReferenceStructures(json.results)
            })
    }, [])

    useEffect(() => {
        if ( isNew) {
            return
        }
        fetchObject()

        fetch('/v0/projects?is_archived=0&project_configuration_scheme_id='+objID, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setUsedInProjects(json.results)
            })
    }, [isNew])

    useEffect(() => {
        let ref_structs = []
        cadmObjects.map(function(cadm_obj) {
            if( ! cadm_obj.do_use_reference_structure ) {
                return
            }

            let ref = {
                cadm_name: cadm_obj.name,
                cadm_id: cadm_obj.id,
                reference_structures: []
            }
            allReferenceStructures?.map(function(ref_obj) {
                if (ref_obj.cadm_object_id !== cadm_obj.id) {
                    return
                }

                ref.reference_structures.push({
                    reference_structure_id: ref_obj.id,
                    reference_structure_name: ref_obj.name
                })

                // really lame way to find the ones currently selected
                if (projectConfigurationSchemeReferenceStructures) {
                    var selected = projectConfigurationSchemeReferenceStructures.find(function (item) { return item.reference_structure_id === ref_obj.id })
                    if (selected) {
                        ref.selected_reference_structure_id = selected.reference_structure_id
                    }
                }

            })

            ref_structs.push(ref)
        })
        setWebRefStructList(ref_structs)
    }, [cadmObjects, allReferenceStructures, projectConfigurationSchemeReferenceStructures])

    useEffect(() => {
        let map_lists = []
        cadmObjects.map(function(cadm_obj) {
            if( ! cadm_obj.do_use_typecode_group_mapping ) {
                return
            }

            let ref = {
                cadm_name: cadm_obj.name,
                cadm_id: cadm_obj.id,
                typecode_group_mappings: []
            }
            allTypecodeGroupMappings?.map(function(ref_obj) {
                if (ref_obj.cadm_object_id !== cadm_obj.id) {
                    return
                }

                ref.typecode_group_mappings.push({
                    typecode_group_mapping_id: ref_obj.id,
                    typecode_group_mapping_name: ref_obj.name
                })

                // really lame way to find the ones currently selected
                if (projectConfigurationSchemeTypecodeGroupMappings) {
                    var selected = projectConfigurationSchemeTypecodeGroupMappings.find(function (item) { return item.typecode_group_mapping_id === ref_obj.id })
                    if (selected) {
                        ref.selected_typecode_group_mapping_id = selected.typecode_group_mapping_id
                    }
                }

            })

            map_lists.push(ref)
        })
        setWebTypecodeMapList(map_lists)
    }, [cadmObjects, allTypecodeGroupMappings, projectConfigurationSchemeTypecodeGroupMappings])

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                throw new Error(json.error)
            }

            fromBackend(json.result)
        })
        .catch(function(error) {
            console.log(error)
        })
    }

    const fromBackend = (json) => {
        setName(json.name)
        setDescription(json.description)
        setDocumentStartNumberingFrom(json.document_start_numbering_from)
        setRecordStartNumberingFrom(json.record_start_numbering_from)
        setProjectConfigurationSchemeReferenceStructures(json.project_configuration_scheme_reference_structures)
        setProjectConfigurationSchemeTypecodeGroupMappings(json.project_configuration_scheme_typecode_group_mappings)

        setIsUsed(json.is_used)
        setDependingObjects(json.depending_objects)
    }

    const toBackend = () => {
        return {
            name: name,
            description: description,
            document_start_numbering_from: parseInt(documentStartNumberingFrom, 10),
            record_start_numbering_from: parseInt(recordStartNumberingFrom, 10),
            project_configuration_scheme_typecode_group_mappings: projectConfigurationSchemeTypecodeGroupMappings,
            project_configuration_scheme_reference_structures: projectConfigurationSchemeReferenceStructures
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if ( ! name ) {
            errors.push('Name is required')
        }

        if ( ! description ) {
            errors.push('Description is required')
        }

        const structures = projectConfigurationSchemeReferenceStructures || [ {} ]
        structures.forEach(function(item) {
            if( ! item.reference_structure_id ) {
                errors.push('You must enter a Reference structure for each Configured Item')
            }
        })
        const mappings = projectConfigurationSchemeTypecodeGroupMappings || [ {} ]
        projectConfigurationSchemeTypecodeGroupMappings.forEach(function(item) {
            if( ! item.typecode_group_mapping_id ) {
                errors.push('You must enter a Typecode map for each Configured Item')
            }
        })

        if( parseInt(Number(documentStartNumberingFrom)) !== documentStartNumberingFrom ) {
            errors.push('Documents Number From must be a number')
        }
        
        if( parseInt(Number(recordStartNumberingFrom)) !== recordStartNumberingFrom ) {
            errors.push('Records Number From must be a number')
        }
        
        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'project_configuration_schemes',
            objID: objID,
            data: toBackend(),
        }
    }

    const clone = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setCloneActive(true)

        let url = objectUrl+'/clone'
        return {
            objType: 'project_configuration_schemes',
            objID: objID,
            url: url,
            data: toBackend(),
        }
    }

    const updateTypecodeMapList = (item, value) => {
        let list = webTypecodeMapList.map(function (m) {
            if(m.cadm_id === item.cadm_id) {
                m.selected_typecode_group_mapping_id = value
            }
            return m;
        });

        // Also update projectConfigurationSchemeTypecodeGroupMappings
        let mapLists = list.map(function (m) {
            return { typecode_group_mapping_id: m.selected_typecode_group_mapping_id }
        })
        setWebTypecodeMapList(list)
        setProjectConfigurationSchemeTypecodeGroupMappings(mapLists)
    }

    const updateWebRefStructList = (item, value) => {
        let list = webRefStructList.map(function (m) {
            if(m.cadm_id === item.cadm_id) {
                m.selected_reference_structure_id = value
            }
            return m;
        });

        // Also update projectConfigurationSchemeReferenceStructures
        let refStructs = list.map(function (m) {
            return { reference_structure_id: m.selected_reference_structure_id }
        })
        setWebRefStructList(list)
        setProjectConfigurationSchemeReferenceStructures(refStructs)
    }

    const removeNonDigits = (input) => {
        let value = parseInt(input, 10)
        if( isNaN(value) ) {
            value = ''
        }
        return value
    }
    
    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Configuration Scheme: {name || '<New Configuration Scheme>'}</h2>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="name" value={name} onChange={e => setName(e.target.value)} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Description
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="description" value={description} onChange={e => setDescription(e.target.value)} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Reference Structures
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <div>
                                            <table className="table table-striped table-bordered table-hover dataTable" >
                                                <thead>
                                                    <tr>
                                                        <th>Configured Item</th>
                                                        <th>Reference Structure</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {webRefStructList.map(function(item) {
                                                        return (
                                                            <tr key={item.cadm_id}>
                                                                <td>
                                                                    {item.cadm_name}
                                                                </td>
                                                                <td>
                                                                    <select className="form-control"
                                                                        value={item.selected_reference_structure_id}
                                                                        onChange={e => updateWebRefStructList(item, parseInt(e.target.value))}
                                                                        required
                                                                        disabled={isUsed && item.selected_reference_structure_id}
                                                                    >
                                                                        <option value=""></option>
                                                                        {item.reference_structures.map(function(ref) {
                                                                            return <option key={ref.reference_structure_id} value={ref.reference_structure_id}>{ref.reference_structure_name}</option>;
                                                                        })}
                                                                    </select>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>Configured Item</th>
                                                        <th>Reference Structure</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Documents Number From
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={documentStartNumberingFrom}
                                            onChange={e => setDocumentStartNumberingFrom( removeNonDigits(e.target.value) )}
                                            required />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" >
                                                The variable $DocumentNumber in the Reference Structure starts at this number.
                                            </small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Records Number From
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={recordStartNumberingFrom}
                                            onChange={e => setRecordStartNumberingFrom( removeNonDigits(e.target.value) )}
                                            required />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" >
                                                The variable $RecordNumber in the Reference Structure starts at this number.
                                            </small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Type-Code Maps
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <div>
                                            <table className="table table-striped table-bordered table-hover dataTable" >
                                                <thead>
                                                    <tr>
                                                        <th>Configured Item</th>
                                                        <th>Type-Code Map</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {webTypecodeMapList.map(function(item) {
                                                        return (
                                                            <tr key={item.cadm_id}>
                                                                <td>
                                                                    {item.cadm_name}
                                                                </td>
                                                                <td>
                                                                    <select className="form-control"
                                                                        value={item.selected_typecode_group_mapping_id}
                                                                        onChange={e => updateTypecodeMapList(item, parseInt(e.target.value))}
                                                                        required
                                                                        disabled={isUsed && item.selected_typecode_group_mapping_id}
                                                                    >
                                                                        <option value=""></option>
                                                                        {item.typecode_group_mappings.map(function(ref) {
                                                                            return <option key={ref.typecode_group_mapping_id} value={ref.typecode_group_mapping_id}>{ref.typecode_group_mapping_name}</option>;
                                                                        })}
                                                                    </select>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>Configured Item</th>
                                                        <th>Type-Code Map</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                {usedInProjects &&

                                    <div className="form-group">
                                        <label className="col-sm-2 control-label"></label>
                                        <div className="col-sm-8">
                                            <label className="control-label">This Scheme is used in the following</label>
                                            <ul className="list-group clear-list m-t">
                                                {usedInProjects.length === 0 &&
                                                <li className="list-group-item">
                                                    No Projects or Products
                                                </li>
                                                }
                                                {usedInProjects.map(function(item) {
                                                    return (
                                                        <li key={item.id} className="list-group-item">
                                                            <Link to={`/portfolio/${item.id}/dashboard`}>
                                                                {item.project_code} - {item.name}
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                }

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        <SaveButton
                                            buttonVariant='warning'
                                            visible={!isNew}
                                            spinnerActive={cloneActive}
                                            label='Clone'
                                            onClick={clone}
                                            onError={handleSubmitError}
                                        />

                                        <DependingObjects list={dependingObjects} />

                                        <DeleteButton
                                           isHidden={isNew || isUsed}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />
                                        
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ProjectConfigurationScheme
